import {
  type IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {useEnvironment} from '@wix/yoshi-flow-editor';
import classNames from 'classnames';
import React, {type CSSProperties, type FC, useEffect, useMemo, useState} from 'react';
import {A11y, Navigation} from 'swiper/modules';
import {Swiper, type SwiperClass, SwiperSlide} from 'swiper/react';
import {SwiperOptions} from 'swiper/types';
import {Alignment} from '../../../../constants';
import {type ProvidedGlobalProps, withGlobalProps} from '../../../../providers/globalPropsProvider';
import {SPECS} from '../../../../specs';
import type {IMediaItem} from '../../../../types/app-types';
import {useProductGallery} from '../../ProductGalleryProvider/ProductGalleryProvider';
import {ArrowButton} from './ArrowButton/ArrowButton';
import s from './ProductMediaSwiper.scss';
import {Thumbnail} from './Thumbnail/Thumbnail';
import {NEXT_ARROW_BUTTON_ID, PREV_ARROW_BUTTON_ID, SWIPER_SPEED} from './utils/constants';
import {getArrowDirection} from './utils/getArrowDirection';
import {useHasVisibleArrows} from './utils/hooks/useHasVisibleArrows';
import {useSlidesOverflow} from './utils/hooks/useSlidesOverflow';
import {useSwiperNavigation} from './utils/hooks/useSwiperNavigation';

function computeSwiperWrapperClasses(isVertical: boolean, thumbnailsOverflow: boolean, align: Alignment): string {
  const allThumbnailsFitInView = !isVertical && !thumbnailsOverflow;
  const shouldCenterAlign = allThumbnailsFitInView && align === Alignment.CENTER;
  const shouldStartAlign = allThumbnailsFitInView && align === Alignment.START;
  return classNames(s.swiperWrapper, {
    [s.swiperWrapperCenterAlignment]: shouldCenterAlign,
    [s.swiperWrapperStartAlignment]: shouldStartAlign,
    [s.swiperWrapperVerticalSSR]: isVertical,
  });
}

function computeSwiperParams(isMainMediaNavArrowsEnabled: boolean, isMobile: boolean, isVertical: boolean) {
  const swiperModules = isMainMediaNavArrowsEnabled ? [A11y] : [Navigation, A11y];
  const swiperNavigation = isMainMediaNavArrowsEnabled
    ? undefined
    : {prevEl: `#${PREV_ARROW_BUTTON_ID}`, nextEl: `#${NEXT_ARROW_BUTTON_ID}`};
  const allowTouchMove = isMainMediaNavArrowsEnabled && isMobile;
  const direction: SwiperOptions['direction'] = isVertical ? 'vertical' : 'horizontal';
  const showPrevArrow = !isMainMediaNavArrowsEnabled;
  const showNextArrow = !isMainMediaNavArrowsEnabled || !isMobile;
  return {swiperModules, swiperNavigation, allowTouchMove, direction, showPrevArrow, showNextArrow};
}

export enum ProductMediaSwiperDataHooks {
  Root = 'product-media-swiper-root',
  PrevArrow = 'product-media-swiper-prev-arrow',
  NextArrow = 'product-media-swiper-next-arrow',
}
export interface ProductMediaSwiperProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  media: IMediaItem[];
  isVertical: boolean;
  align: Alignment;
}

const ProductMediaSwiperComponent: FC<ProductMediaSwiperProps> = ({
  media,
  isVertical,
  align,
  t,
  globals: {useExperiments},
}) => {
  const {changeSelectedIndex, selectedIndex} = useProductGallery();
  const [swiper, setSwiper] = useState<SwiperClass>(null);
  const {prevNavigation, nextNavigation, handleKeyDown} = useSwiperNavigation(media.length, swiper, isVertical);

  const hasVisibleArrows = useHasVisibleArrows(swiper);
  const slidesOverflow = useSlidesOverflow(swiper);
  const isMainMediaNavArrowsEnabled = useExperiments.enabled(SPECS.ProductPageMainMediaNavigationArrows);
  const isFixTotalSwiperSizeCalc = useExperiments.enabled(SPECS.FixPPTotalSwiperSizeCalc);

  const thumbnailsOverflow = isMainMediaNavArrowsEnabled ? slidesOverflow : hasVisibleArrows;

  const {isRTL, isMobile} = useEnvironment();

  useEffect(() => {
    const shouldSlideToSelectedIndex = isMainMediaNavArrowsEnabled && slidesOverflow && !isMobile;

    if (shouldSlideToSelectedIndex) {
      swiper?.slideToLoop(selectedIndex);
    }
  }, [selectedIndex, isMainMediaNavArrowsEnabled, slidesOverflow, swiper, isMobile]);

  const swiperRootClasses = useMemo(
    () =>
      classNames(s.swiperRoot, {
        [s.swiperRootHorizontal]: !isVertical,
        [s.swiperRootVertical]: isVertical,
        [s.shouldRemovePrevArrow]: isMainMediaNavArrowsEnabled,
      }),
    [isVertical, isMainMediaNavArrowsEnabled]
  );

  const swiperWrapperClasses = computeSwiperWrapperClasses(isVertical, thumbnailsOverflow, align);

  /* istanbul ignore next */
  useEffect(() => {
    if (!swiper?.wrapperEl) {
      return;
    }
    setTimeout(() => swiper?.wrapperEl?.classList.remove(s.swiperWrapperVerticalSSR), 0);
  }, [swiper]);

  const swiperSlideClasses = useMemo(() => {
    return classNames(s.swiperSlide, s.swiperSlideThumbnail, {
      [s.swiperSlideThumbnailHorizontal]: !isVertical,
      [s.swiperSlideThumbnailVertical]: isVertical,
    });
  }, [isVertical]);

  const inlineCssVars = {'--media-length': `${media.length}`};
  const {swiperModules, swiperNavigation, allowTouchMove, direction, showPrevArrow, showNextArrow} =
    computeSwiperParams(isMainMediaNavArrowsEnabled, isMobile, isVertical);

  return (
    <div
      data-hook={ProductMediaSwiperDataHooks.Root}
      style={inlineCssVars as CSSProperties}
      className={classNames(s.root, {
        [s.rootVertical]: isVertical,
        [s.hasNextArrow]: showNextArrow,
        [s.fixTotalSwiperSizeCalc]: isFixTotalSwiperSizeCalc,
      })}
      {...(thumbnailsOverflow && {onKeyDown: handleKeyDown})}>
      {showPrevArrow && (
        <ArrowButton
          id={PREV_ARROW_BUTTON_ID}
          dataHook={ProductMediaSwiperDataHooks.PrevArrow}
          onClick={prevNavigation}
          isVertical={isVertical}
          direction={getArrowDirection(true, isVertical, isRTL)}
        />
      )}
      <Swiper
        onSwiper={setSwiper}
        speed={SWIPER_SPEED}
        direction={direction}
        className={swiperRootClasses}
        wrapperClass={swiperWrapperClasses}
        modules={swiperModules}
        navigation={swiperNavigation}
        a11y={{
          prevSlideMessage: t('productPage.slideGallery.previous.ariaLabel'),
          nextSlideMessage: t('productPage.slideGallery.next.ariaLabel'),
        }}
        loop={true}
        slidesPerView="auto"
        watchSlidesProgress
        allowTouchMove={allowTouchMove}
        spaceBetween={0}>
        {media.map((mediaItem, index) => (
          <SwiperSlide className={swiperSlideClasses} key={index} onClick={() => changeSelectedIndex(index)}>
            <Thumbnail mediaItem={mediaItem} index={index} />
          </SwiperSlide>
        ))}
      </Swiper>
      {showNextArrow && (
        <ArrowButton
          id={NEXT_ARROW_BUTTON_ID}
          dataHook={ProductMediaSwiperDataHooks.NextArrow}
          onClick={nextNavigation}
          isVertical={isVertical}
          direction={getArrowDirection(false, isVertical, isRTL)}
        />
      )}
    </div>
  );
};

export const ProductMediaSwiper = withGlobalProps(withTranslations('globals.texts')(ProductMediaSwiperComponent));
